import { FormGroupHeader, AlertDialogComponent } from "@/components/shared";
import { ActionButtonConfig } from "@/components/shared/FormGroupHeader";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import NewSheetAgentDialog from "./NewSheetAgentDialog";
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemTitle,
} from "@/components/ui/item";
import { ChevronRightIcon, EllipsisVerticalIcon, FileSpreadsheetIcon } from "lucide-react";
import { Spinner } from "@/components/ui/spinner";
import {
  SheetAgentRun,
  SheetAgentRunStatus,
} from "@/graphql/__generated__/graphql";
import { useGetSheetAgentRunsByTenderId } from "@/hooks/queries/useGetSheetAgentRunsByTenderId";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from "@/components/ui/empty";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toNormalCase } from "@/utils/helpers";
import DownloadUrlDialog from "./DownloadUrlDialog";
import useDeleteSheetAgentRunMutationHook from "@/hooks/mutations/useDeleteSheetAgentRunMutation";
import SheetAgentHelpDialog from "./SheetAgentHelpDialog/SheetAgentHelpDialog";
import { useTenderPermissionsData } from "@/hooks/cache/useTenderPermissionsData";

/**
 * Returns the badge variant, custom text, and spinner flag for a sheet agent run status
 * @param status - The status of the sheet agent run
 * @param t - Translation function
 * @returns Object with variant, text, and showSpinner flag
 */
const getStatusBadgeConfig = (
  status: SheetAgentRunStatus,
  t: TFunction
): {
  variant: "default" | "secondary" | "destructive" | "outline";
  text: string;
  showSpinner: boolean;
} => {
  let variant: "default" | "secondary" | "destructive" | "outline";
  let text: string;
  let showSpinner = false;

  switch (status) {
  case SheetAgentRunStatus.Completed:
    variant = "default";
    text = t("edittender.sheetagent.page.status.completed");
    break;
  case SheetAgentRunStatus.Failed:
    variant = "destructive";
    text = t("edittender.sheetagent.page.status.failed");
    break;
  case SheetAgentRunStatus.Pending:
    variant = "secondary";
    text = t("edittender.sheetagent.page.status.pending");
    break;
  case SheetAgentRunStatus.ExtractingTasks:
    variant = "secondary";
    text = t("edittender.sheetagent.page.status.extractingtasks");
    showSpinner = true;
    break;
  case SheetAgentRunStatus.UpdatingTasks:
    variant = "secondary";
    text = t("edittender.sheetagent.page.status.tasksready");
    break;
  default:
    variant = "outline";
    text = toNormalCase(status) || String(status);
  }

  return { variant, text, showSpinner };
};

/**
 * Page component that displays the sheet agent for a tender
 * @returns A page component with the sheet agent
 */
function SheetAgentPage() {
  const { t } = useTranslation();
  const { tenderID } = useParams<{ tenderID: string }>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [runIdToDelete, setRunIdToDelete] = useState<number | null>(null);
  const { deleteSheetAgentRunMutation } = useDeleteSheetAgentRunMutationHook();
  const { canManageSheetAgent } = useTenderPermissionsData(Number(tenderID));

  const buttonConfig: ActionButtonConfig[] = canManageSheetAgent ? [
    {
      text: t("edittender.sheetagent.btnnewsheet"),
      variant: "default",
      actionFn: () => {
        setDialogOpen(true);
      }
    }
  ] : [];

  const { data, loading, refetch } = useGetSheetAgentRunsByTenderId(tenderID || "");
  const runs = data?.getSheetAgentRunsByTenderId ?? [];

  const handleDelete = (runId: number, e: React.MouseEvent) => {
    // Prevent item on click navigation
    e.preventDefault();
    e.stopPropagation();
    setRunIdToDelete(runId);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (runIdToDelete === null) return;
    await deleteSheetAgentRunMutation({
      variables: {
        deleteSheetAgentRunId: runIdToDelete,
      },
    });
    setDeleteDialogOpen(false);
    setRunIdToDelete(null);
  };

  const showDownloadUrlsAction = (run: Partial<SheetAgentRun>) => {
    return (
      run.status === SheetAgentRunStatus.Completed &&
      Boolean(run.downloadUrls)
    );
  };

  const showDeleteAction = (run: Partial<SheetAgentRun>) => {
    return (
      canManageSheetAgent &&
      (
        run.status === SheetAgentRunStatus.Completed ||
        run.status === SheetAgentRunStatus.UpdatingTasks ||
        run.status === SheetAgentRunStatus.Failed
      )
    );
  };

  const showDropDownMenu = (run: Partial<SheetAgentRun>) => {
    return (
      showDownloadUrlsAction(run) ||
      showDeleteAction(run)
    );
  };

  return (
    <div>
      <FormGroupHeader
        title={t("edittender.sheetagent.title")}
        description={t("edittender.sheetagent.description")}
        className="pr-4 pl-2"
        actionButtons={buttonConfig}
      >
        <SheetAgentHelpDialog />
      </FormGroupHeader>
      <div className="flex flex-col gap-4 pb-[300px] pr-4 pl-2">
        {loading && runs.length === 0 ? (
          <div className="flex items-center justify-center p-8">
            <Spinner />
          </div>
        ) : runs.length === 0 ? (
          <Empty>
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileSpreadsheetIcon />
              </EmptyMedia>
              <EmptyTitle>{t("edittender.sheetagent.noruns")}</EmptyTitle>
              <EmptyDescription>
                {t("edittender.sheetagent.norunsdescription")}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button
                variant="default"
                onClick={() => {
                  setDialogOpen(true);
                }}>
                {t("edittender.sheetagent.btnnewsheet")}
              </Button>
            </EmptyContent>
          </Empty>
        ) : (
          runs.map((run) => {
            const isClickable =
              run.status === SheetAgentRunStatus.Completed ||
              run.status === SheetAgentRunStatus.UpdatingTasks;
            const statusBadge = getStatusBadgeConfig(run.status, t);
            const itemContent = (
              <>
                <ItemContent>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <ItemTitle>{run.name}</ItemTitle>
                      <ItemDescription>
                        {run.description || t("edittender.sheetagent.nodescription")}
                      </ItemDescription>
                    </div>
                    <Badge variant={statusBadge.variant} className="flex items-center gap-1.5">
                      {statusBadge.showSpinner && (
                        <Spinner className="h-3 w-3" />
                      )}
                      {statusBadge.text}
                    </Badge>
                  </div>
                </ItemContent>
                <ItemActions>
                  {showDropDownMenu(run) && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                          }}
                        >
                          <EllipsisVerticalIcon className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {showDownloadUrlsAction(run) && (
                          <DropdownMenuItem asChild>
                            <div>
                              <DownloadUrlDialog
                                downloadUrls={run.downloadUrls}
                                iconDisplay="text"
                                textOnlyTrigger={true}
                              />
                            </div>
                          </DropdownMenuItem>
                        )}
                        {showDeleteAction(run) && (
                          <DropdownMenuItem
                            className="text-red-500 cursor-pointer"
                            onClick={(e) => handleDelete(run.id, e)}
                          >
                            {t("common:buttons.remove")}
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  {isClickable && (
                    <ChevronRightIcon className="size-4" />
                  )}
                </ItemActions>
              </>
            );

            return (
              <Item key={run.id} asChild={isClickable} variant="outline">
                {isClickable ? (
                  <Link to={`/tender/${tenderID}/sheet-agent/${run.id}`}>
                    {itemContent}
                  </Link>
                ) : (
                  <>{itemContent}</>
                )}
              </Item>
            );
          })
        )}
      </div>
      {canManageSheetAgent && (
        <NewSheetAgentDialog open={dialogOpen} onOpenChange={setDialogOpen} onSuccess={refetch} />
      )}
      <AlertDialogComponent
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        bodyText={t("edittender.sheetagent.confirmdelete")}
        title={t("edittender.sheetagent.confirmdeletetitle")}
        actionButtonText={t("common:buttons.confirm")}
        actionButtonFn={confirmDelete}
      />
    </div>
  );
}

export default SheetAgentPage;
